import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Tab {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  closable?: boolean;
  params?: Record<string, string>;
}

interface TabState {
  tabs: Tab[];
  activeTabId: string | null;
  maxTabs: number;
}

type TabAction =
  | { type: 'ADD_TAB'; payload: Tab }
  | { type: 'REMOVE_TAB'; payload: string }
  | { type: 'SET_ACTIVE_TAB'; payload: string }
  | { type: 'REORDER_TABS'; payload: { sourceIndex: number; destinationIndex: number } }
  | { type: 'CLOSE_OTHER_TABS'; payload: string }
  | { type: 'CLOSE_TABS_TO_RIGHT'; payload: string }
  | { type: 'RESTORE_STATE'; payload: TabState };

const tabReducer = (state: TabState, action: TabAction): TabState => {
  switch (action.type) {
    case 'ADD_TAB': {
      const existingTab = state.tabs.find(tab => tab.path === action.payload.path);
      
      if (existingTab) {
        return {
          ...state,
          activeTabId: existingTab.id
        };
      }

      let newTabs = [...state.tabs];
      
      // If we're at max tabs, remove the oldest non-active tab
      if (newTabs.length >= state.maxTabs) {
        const oldestClosableIndex = newTabs.findIndex(tab => 
          tab.closable !== false && tab.id !== state.activeTabId
        );
        if (oldestClosableIndex !== -1) {
          newTabs.splice(oldestClosableIndex, 1);
        } else {
          // If all tabs are non-closable, remove the first one
          newTabs.shift();
        }
      }

      newTabs.push(action.payload);
      
      return {
        ...state,
        tabs: newTabs,
        activeTabId: action.payload.id
      };
    }

    case 'REMOVE_TAB': {
      const tabIndex = state.tabs.findIndex(tab => tab.id === action.payload);
      if (tabIndex === -1) return state;

      const newTabs = state.tabs.filter(tab => tab.id !== action.payload);
      let newActiveTabId = state.activeTabId;

      // If we're closing the active tab, determine the new active tab
      if (state.activeTabId === action.payload) {
        if (newTabs.length === 0) {
          newActiveTabId = null;
        } else {
          // Prefer the tab to the left (before it), or if it was the first tab, go to the right
          const nextTab = newTabs[tabIndex - 1] || newTabs[tabIndex] || newTabs[0];
          newActiveTabId = nextTab?.id || null;
        }
      }

      return {
        ...state,
        tabs: newTabs,
        activeTabId: newActiveTabId
      };
    }

    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTabId: action.payload
      };

    case 'REORDER_TABS': {
      const { sourceIndex, destinationIndex } = action.payload;
      const newTabs = [...state.tabs];
      const [movedTab] = newTabs.splice(sourceIndex, 1);
      newTabs.splice(destinationIndex, 0, movedTab);

      return {
        ...state,
        tabs: newTabs
      };
    }

    case 'CLOSE_OTHER_TABS': {
      const targetTab = state.tabs.find(tab => tab.id === action.payload);
      if (!targetTab) return state;

      return {
        ...state,
        tabs: [targetTab],
        activeTabId: action.payload
      };
    }

    case 'CLOSE_TABS_TO_RIGHT': {
      const targetIndex = state.tabs.findIndex(tab => tab.id === action.payload);
      if (targetIndex === -1) return state;

      const newTabs = state.tabs.slice(0, targetIndex + 1);
      const newActiveTabId = newTabs.some(tab => tab.id === state.activeTabId) 
        ? state.activeTabId 
        : action.payload;

      return {
        ...state,
        tabs: newTabs,
        activeTabId: newActiveTabId
      };
    }

    case 'RESTORE_STATE':
      return action.payload;

    default:
      return state;
  }
};

interface TabContextType {
  tabs: Tab[];
  activeTabId: string | null;
  activeTab: Tab | null;
  addTab: (tab: Omit<Tab, 'id'>) => string;
  removeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  reorderTabs: (sourceIndex: number, destinationIndex: number) => void;
  closeOtherTabs: (tabId: string) => void;
  closeTabsToRight: (tabId: string) => void;
  isTabActive: (tabId: string) => boolean;
  navigateToTab: (tabId: string) => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const useTab = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within a TabProvider');
  }
  return context;
};

interface TabProviderProps {
  children: ReactNode;
  maxTabs?: number;
  persistTabs?: boolean;
}

const STORAGE_KEY = 'warehouse-tabs';

export const TabProvider: React.FC<TabProviderProps> = ({ 
  children, 
  maxTabs = 8,
  persistTabs = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialState: TabState = {
    tabs: [],
    activeTabId: null,
    maxTabs
  };

  const [state, dispatch] = useReducer(tabReducer, initialState);

  // Load persisted state on mount
  useEffect(() => {
    if (persistTabs) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsedState = JSON.parse(saved);
          dispatch({ type: 'RESTORE_STATE', payload: { ...parsedState, maxTabs } });
        }
      } catch (error) {
        console.warn('Failed to load tab state from localStorage:', error);
      }
    }
  }, [maxTabs, persistTabs]);

  // Persist state changes
  useEffect(() => {
    if (persistTabs && state.tabs.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.warn('Failed to save tab state to localStorage:', error);
      }
    }
  }, [state, persistTabs]);

  const generateTabId = useCallback(() => {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addTab = useCallback((tabData: Omit<Tab, 'id'>): string => {
    const id = generateTabId();
    const tab: Tab = {
      id,
      closable: true,
      ...tabData
    };
    
    dispatch({ type: 'ADD_TAB', payload: tab });
    return id;
  }, [generateTabId]);

  const removeTab = useCallback((tabId: string) => {
    // Prevent closing the last tab
    if (state.tabs.length <= 1) return;
    
    dispatch({ type: 'REMOVE_TAB', payload: tabId });
  }, [state.tabs.length]);

  const setActiveTab = useCallback((tabId: string) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tabId });
  }, []);

  const reorderTabs = useCallback((sourceIndex: number, destinationIndex: number) => {
    dispatch({ type: 'REORDER_TABS', payload: { sourceIndex, destinationIndex } });
  }, []);

  const closeOtherTabs = useCallback((tabId: string) => {
    dispatch({ type: 'CLOSE_OTHER_TABS', payload: tabId });
  }, []);

  const closeTabsToRight = useCallback((tabId: string) => {
    dispatch({ type: 'CLOSE_TABS_TO_RIGHT', payload: tabId });
  }, []);

  const isTabActive = useCallback((tabId: string) => {
    return state.activeTabId === tabId;
  }, [state.activeTabId]);

  const navigateToTab = useCallback((tabId: string) => {
    const tab = state.tabs.find(t => t.id === tabId);
    if (tab) {
      setActiveTab(tabId);
      navigate(tab.path);
    }
  }, [state.tabs, navigate, setActiveTab]);

  const activeTab = state.tabs.find(tab => tab.id === state.activeTabId) || null;

  const contextValue: TabContextType = {
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    activeTab,
    addTab,
    removeTab,
    setActiveTab,
    reorderTabs,
    closeOtherTabs,
    closeTabsToRight,
    isTabActive,
    navigateToTab
  };

  return (
    <TabContext.Provider value={contextValue}>
      {children}
    </TabContext.Provider>
  );
};